import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshButton } from '@/components/ui/RefreshButton';
import { RefreshCw, AlertCircle, TrendingUp, Users, CheckCircle, XCircle, Clock, Globe } from 'lucide-react';
import { DateRangeSelector } from '@/components/layout/DateRangeSelector';
import { PageShell } from '@/components/layout/PageShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { useSettingsStore } from '@/store/settingsStore';
import { useContextSwitcher } from '@/features/organizations/hooks';
import { getDateRangeForPreset } from '@/lib/schemas/dateRange.schemas';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

// Import dashboard components
import {
  PanelGroup,
  ValidationsByDateChart,
  Map,
  PendingStepsChart,
  DeviceChart,
  BrowserChart,
  DurationChart,
  RejectionReasonsChart,
  SdkVersionChart,
  IdTypeChart,
  FilterChips
} from '../components';
import { useDashboardFiltersStore, hasActiveFilters } from '@/store/dashboardFiltersStore';



// Import hooks
import { useDashboardData } from '../hooks/useDashboardData';

// Import API functions (only needed types now)
import { 
  getDefaultDashboardParams,
  type DashboardQueryParams 
} from '../api/dashboard.api';



/**
 * DashboardPage Component
 * 
 * Main dashboard page that integrates all dashboard components.
 * Displays metrics, charts, and geographic data with proper loading, error, and empty states.
 * 
 * Requirements: 8.1, 8.4, 8.5, 8.6
 */
export function DashboardPage() {
  const { t } = useTranslation();
  
  // Get date range from store and context switcher
  const { selectedDateRange } = useSettingsStore();
  const { updateDateRange } = useContextSwitcher();
  
  // Get refresh functionality
  const { invalidateDashboard } = useQueryInvalidation();

  // Initialize default date range if none selected
  useEffect(() => {
    if (!selectedDateRange) {
      const defaultRange = getDateRangeForPreset('last_7_days');
      if (defaultRange) {
        updateDateRange(defaultRange);
      }
    }
  }, [selectedDateRange, updateDateRange]);

  // Cross-filter store is shared globally (Zustand) but the dashboard
  // semantics are "filters belong to this view" — wipe them on mount
  // and unmount so navigating away + returning gives a clean slate.
  const clearAllFilters = useDashboardFiltersStore((s) => s.clearAll);
  const activeFilters = useDashboardFiltersStore((s) => s.filters);
  const hasFilters = hasActiveFilters(activeFilters);
  useEffect(() => {
    clearAllFilters();
    return () => clearAllFilters();
  }, [clearAllFilters]);

  // Create stable query parameters to prevent cache misses
  const queryParams: DashboardQueryParams = useMemo(() => {
    const defaultParams = getDefaultDashboardParams();
    if (selectedDateRange) {
      // Convert DateRange to proper API format with timestamps
      const fromDate = selectedDateRange.from instanceof Date 
        ? selectedDateRange.from 
        : new Date(selectedDateRange.from);
      const toDate = selectedDateRange.to instanceof Date 
        ? selectedDateRange.to 
        : new Date(selectedDateRange.to);
      
      return {
        ...defaultParams,
        startDate: fromDate.toISOString(),
        endDate: toDate.toISOString(),
      };
    }
    return defaultParams;
  }, [
    selectedDateRange?.from.getTime(),
    selectedDateRange?.to.getTime(),
  ]);

  // Fetch ALL dashboard data in a single query
  const { 
    data: dashboardData, 
    isLoading, 
    isError, 
    error 
  } = useDashboardData(queryParams);

  // Calculate status from the main query data
  const isEmpty = !isLoading && !isError && !!dashboardData && dashboardData.metrics.total_validations === 0;
  const metricsError = isError;
  const metricsErrorDetails = error;
  const metrics = dashboardData?.metrics;


  // Extract chart data from consolidated response
  const validationsByDateChart = dashboardData?.charts.validationsByDate;
  const geographicData = dashboardData?.charts.geographicData;
  const pendingStepsData = dashboardData?.charts.pendingSteps;
  const deviceData = dashboardData?.charts.deviceData;
  const browserData = dashboardData?.charts.browserData;
  const durationData = dashboardData?.charts.durationData;
  const rejectionReasonsData = dashboardData?.charts.rejectionReasons;
  const sdkVersionsData = dashboardData?.charts.sdkVersions;
  const idTypesData = dashboardData?.charts.idTypes;

  // All loading states are now unified
  const isAnyLoading = isLoading;

  // Handle refresh all data
  const handleRefreshAll = () => {
    console.log('🔄 DashboardPage: Refresh button clicked');
    invalidateDashboard();
  };

  const dashboardHeader = (extraRefreshProps?: { refreshText?: string; refreshingText?: string }) => (
    <PageHeader
      title={t('dashboard.title')}
      subtitle={t('dashboard.subtitle')}
      actions={
        <>
          <DateRangeSelector
            selectedRange={selectedDateRange}
            onRangeChange={updateDateRange}
            maxDays={31}
          />
          <RefreshButton
            onRefresh={handleRefreshAll}
            isRefreshing={isLoading}
            size="sm"
            {...extraRefreshProps}
          />
        </>
      }
    />
  );

  // Handle error state - show error if metrics fail to load
  if (metricsError && metricsErrorDetails) {
    return (
      <PageShell
        header={dashboardHeader({
          refreshText: t('common.retry'),
          refreshingText: t('common.refreshing'),
        })}
        bodyClassName="space-y-6"
      >
        {/* Keep chips reachable in error state too — if a bad filter
            caused the upstream to fail, the user must still be able to
            clear it without refreshing the page. */}
        <FilterChips />
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-2xl flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-semibold">{t('dashboard.errorLoadingData')}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {metricsErrorDetails.message}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleRefreshAll} className="h-10">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('common.retry')}
              </Button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // Handle empty state - show when metrics loaded successfully but no data.
  // CRITICAL: render FilterChips above so the user can clear filters that
  // caused the empty result. Without this, an active filter producing 0
  // matches becomes inescapable without a page refresh.
  if (isEmpty) {
    return (
      <PageShell header={dashboardHeader()} bodyClassName="space-y-6">
        <FilterChips />
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center">
              <TrendingUp className="h-8 w-8 text-primary/60" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base font-semibold">{t('dashboard.noDataAvailable')}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {hasFilters
                  ? t(
                      'dashboard.noDataWithFilters',
                      "No verifications match the active filters. Clear them to see all data.",
                    )
                  : "We couldn't find any validation data for the selected time range. Try adjusting your date range or check back later."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {hasFilters && (
                <Button onClick={clearAllFilters} className="h-10">
                  {t('dashboard.filters.clearAll', 'Clear all')}
                </Button>
              )}
              <Button
                onClick={handleRefreshAll}
                variant={hasFilters ? 'outline' : 'default'}
                className="h-10"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Data
              </Button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      header={
        <PageHeader
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
          actions={
            <>
              <DateRangeSelector
                selectedRange={selectedDateRange}
                onRangeChange={updateDateRange}
                maxDays={31}
              />
              <div className="flex items-center gap-2">
                {isAnyLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                    <span>{t('common.loading')}</span>
                  </div>
                )}
                <RefreshButton
                  onRefresh={handleRefreshAll}
                  isRefreshing={isAnyLoading}
                  variant="outline"
                  size="sm"
                  className="h-10"
                  refreshText={t('common.refresh')}
                  refreshingText={t('common.refreshing')}
                />
              </div>
            </>
          }
        />
      }
      bodyClassName="space-y-6"
    >


      {/* Active cross-filter chips (auto-hides when no filters) */}
      <FilterChips />

      {/* Metrics Panel Group */}
      {metrics && (
        <PanelGroup
          metrics={metrics}
          loading={isLoading}
        />
      )}

      {/* Main Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Pending Steps Breakdown */}
        <div className="lg:col-span-1">
          <div className="h-[420px]">
            <PendingStepsChart 
              data={pendingStepsData}
              loading={isAnyLoading}
              error={isError ? error : undefined}
            />
          </div>
        </div>

        {/* Validations by Date Chart */}
        <div className="lg:col-span-2">
          <div className="h-[420px]">
            {validationsByDateChart ? (
              <ValidationsByDateChart 
                data={validationsByDateChart}
                loading={isAnyLoading}
                error={isError ? error : undefined}
              />
            ) : (
              <Card className="h-full flex flex-col">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="text-base">{t('dashboard.validationsByDate')}</CardTitle>
                  <CardDescription>Validation trends over time</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="border-2 border-dashed border-muted rounded p-8 text-center">
                    {isAnyLoading ? (
                      <div className="text-center text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        {t('dashboard.loadingChart')}
                      </div>
                    ) : isError ? (
                      <div className="text-center text-muted-foreground">
                        <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                        <p>{t('dashboard.failedToLoadChart')}</p>
                        <Button 
                          onClick={handleRefreshAll} 
                          variant="outline" 
                          size="sm" 
                          className="mt-2"
                        >
                          {t('common.retry')}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground">
                        <TrendingUp className="h-6 w-6 mx-auto mb-2" />
                        <p>{t('dashboard.chartWillAppear')}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Row - Equal Height Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Rejection Reasons composition */}
        <div className="h-[380px]">
          <RejectionReasonsChart
            data={rejectionReasonsData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
          />
        </div>

        {/* Top Devices */}
        <div className="h-[380px]">
          <DeviceChart
            data={deviceData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
          />
        </div>

        {/* Browser Usage */}
        <div className="h-[380px]">
          <BrowserChart
            data={browserData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
          />
        </div>

        {/* Validation Duration */}
        <div className="h-[380px]">
          <DurationChart
            data={durationData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
          />
        </div>
      </div>

      {/* SDK versions + Document types — paired row, both cross-filter ON.
          Each list drills the entire dashboard to its selected dimension. */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="h-[420px]">
          <SdkVersionChart
            data={sdkVersionsData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
            enableCrossFilter
          />
        </div>
        <div className="h-[420px]">
          <IdTypeChart
            data={idTypesData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
            enableCrossFilter
          />
        </div>
      </div>

      {/* Geographic Map */}
      <div>
        {geographicData ? (
          <Map 
            data={geographicData}
            loading={isAnyLoading}
            error={isError ? error : undefined}
            height={450}
            title="Geographic Distribution of Validations"
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {t('dashboard.geographicDistribution')}
              </CardTitle>
              <CardDescription>{t('dashboard.validationDistributionByCountry')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center h-[450px] border-2 border-dashed border-muted rounded">
                {isAnyLoading ? (
                  <div className="text-center text-muted-foreground">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    {t('dashboard.loadingMap')}
                  </div>
                ) : isError ? (
                  <div className="text-center text-muted-foreground">
                    <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                    <p>{t('dashboard.failedToLoadGeoData')}</p>
                    <Button 
                      onClick={handleRefreshAll} 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                    >
                      {t('common.retry')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Users className="h-6 w-6 mx-auto mb-2" />
                    <p>{t('dashboard.mapWillAppear')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status Summary Footer */}
      {metrics && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>{t('dashboard.successRate')}: {metrics.validation_rate.toFixed(1)}%</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-yellow-500" />
                  <span>{t('dashboard.pending')}: {metrics.pending_validations.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span>{t('dashboard.rejected')}: {metrics.failed_validations.toLocaleString()}</span>
                </div>
              </div>
              <div className="text-muted-foreground">
                {t('dashboard.period')}: {new Date(metrics.period.start).toLocaleDateString()} - {new Date(metrics.period.end).toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}
